/**
 * Quem é dono das AÇÕES do turno — a regra, numa fonte única.
 *
 * "Ação de passo vencido não pode existir." O caminho de RELOAD já aplicava
 * isso (`rehydratePresentation`, em `../persisted-presentation.ts`): apaga
 * `suggestedActions` de toda mensagem de assistente que não seja a última, com
 * o comentário "Congelado: card sim, ação não." O card fica, porque é registro
 * do que aconteceu; o botão sai, porque é ação.
 *
 * O caminho VIVO não aplicava. Medido (2026-07-26): a fileira de
 * `suggestedActions` do backend em `MessageBubble` (`source="backend"`)
 * renderiza sem portão nenhum, e `SuggestedActions` não tem prop `frozen` —
 * ela só desabilita os botões DEPOIS de um clique na própria instância. Logo,
 * na mesma sessão e sem recarregar, o chip de um ponto de decisão vencido
 * seguia clicável. Recarregar a página "consertava", o que é o pior tipo de
 * inconsistência: o mesmo histórico se comportando de dois jeitos.
 *
 * E havia um segundo lado: o vivo usava `isLast` (última mensagem da LISTA) e o
 * reload usa a última do ASSISTENTE. Quando o usuário responde por escrito, a
 * última da lista passa a ser dele, e o vivo congelava chips que o reload
 * manteria. Duas regras parecidas divergindo no caso mais comum do onboarding.
 *
 * Este módulo é a regra ÚNICA que os dois caminhos consomem. Enquanto cada lado
 * tinha a sua, elas divergiram — é o mesmo padrão que produziu o M10 no motor.
 *
 * PURO: sem I/O, sem React.
 */

/** O mínimo que a regra precisa saber de uma mensagem. */
export interface RoledMessage {
  role: string
  /**
   * `'error'` marca a bolha de um turno que NÃO respondeu (vigia de silêncio,
   * fluxo interrompido, passe que não veio). Opcional: o histórico reidratado
   * do servidor nunca carrega bolha de erro, então o reload segue igual.
   */
  status?: string
}

/**
 * Bolha de erro não vence o ponto de decisão anterior.
 *
 * O caso do Flávio (iPad, 01/09/2026): o toque em "Gestor / Gerente" falhou
 * sem resposta, a tela pôs a bolha de erro embaixo do card, e a bolha — por
 * ser "a última do assistente" — virou a dona das ações. O card de perfil
 * congelou com os cinco chips desabilitados, e o "Tentar novamente" da bolha
 * não reenvia clique de trilho (só reenvia texto do usuário). Resultado: uma
 * conversa morta, sem caminho de volta a não ser abrir outra.
 *
 * Um turno que não respondeu não trouxe pergunta nova; a pergunta corrente
 * continua sendo a do card anterior, e ele tem de seguir aceitando toque.
 */
function isErrorBubble(message: RoledMessage | undefined): boolean {
  return message?.status === 'error'
}

/**
 * Índice da última mensagem do ASSISTENTE, ou `-1` se não houver nenhuma.
 *
 * É a última do assistente, e não a última da lista, porque uma resposta do
 * usuário depois da pergunta não torna a pergunta vencida — ela continua sendo
 * o ponto de decisão corrente.
 */
export function lastAssistantIndex(messages: readonly RoledMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role === 'assistant' && !isErrorBubble(message)) return i
  }
  return -1
}

/**
 * `true` quando a mensagem nesse índice é a dona das ações do turno — ou seja,
 * a única que pode oferecer botão clicável.
 *
 * Turno de usuário nunca é dono: ação é do assistente.
 */
export function ownsTurnActions(messages: readonly RoledMessage[], index: number): boolean {
  const message = messages[index]
  if (!message || message.role !== 'assistant') return false
  return index === lastAssistantIndex(messages)
}
